import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import type { Organisation } from 'src/shared/types/engine/organisation';
import { fetchAllPages } from './fetchAllPages';

/** The engine's maximum page size, so the fewest requests read the whole list. */
const ORGANISATIONS_PER_PAGE = 100;

type OrganisationsPage = {
    items: Organisation[];
    pagination: { hasNext: boolean };
};

/** Active and archived organisations alike: Estate Management compares both with Sunset. */
const organisationsPageRequest = (page: number): FetchArgs => ({
    url: '/org',
    method: 'GET',
    params: { status: 'all', page, page_size: ORGANISATIONS_PER_PAGE },
});

const estateManagementEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        /** No tags: a bulk onboard must not read every page again for each organisation. The page refetches. */
        listAllOrganisations: builder.query<Organisation[], void>({
            queryFn: (_arg, _queryApi, _extraOptions, baseQuery) =>
                fetchAllPages<Organisation, FetchBaseQueryError>(async page => {
                    const response = await baseQuery(organisationsPageRequest(page));
                    if (response.error) {
                        return { error: response.error };
                    } else {
                        const { items, pagination } = response.data as OrganisationsPage;
                        return { data: { items, hasNext: pagination.hasNext } };
                    }
                }),
        }),
    }),
});

export const { useListAllOrganisationsQuery } = estateManagementEngineSlice;

export default estateManagementEngineSlice;
